import axios from 'axios'
import { getAuth } from '@misc'

// Gateboard admin API (asgard-api) and offer-worker endpoints.
const API = process.env.REACT_APP_API_URL
const OFFER = process.env.REACT_APP_OFFER_ENDPOINT

const admin = () => ({ 'Content-Type': 'application/json', ...getAuth() })

// --- switches / users ---
export async function getUsers() {
    const { data } = await axios({ url: `${API}/hotel/users`, method: 'GET', headers: admin() })
    // /hotel/users returns a plain array
    return Array.isArray(data) ? data : data.users || []
}

// suppliers enabled for one switch
export async function getUserSuppliers(userId) {
    const { data } = await axios({
        url: `${API}/hotel/users/${userId}/suppliers`,
        method: 'GET',
        headers: admin()
    })
    const list = data.suppliers || data || []
    // drop inactive (su.del_flag aliased as `inactive`)
    return list.filter(s => !s.inactive)
}

// mint a per-switch offer-worker token (no password needed, server-side)
export async function genToken(id, email) {
    const { data } = await axios({
        url: `${API}/hotel/generate-token`,
        method: 'POST',
        headers: admin(),
        data: { id: `${id}`, email }
    })
    return data.data ? data.data.auth_token : data.auth_token
}

// --- target pickers (new asgard endpoints) ---
export async function searchRegions(q) {
    const { data } = await axios({
        url: `${API}/hotel/regions`,
        method: 'GET',
        params: { q, limit: 20 },
        headers: admin()
    })
    return data.regions || []
}

export async function searchAirports(q) {
    const { data } = await axios({
        url: `${API}/hotel/airports`,
        method: 'GET',
        params: { q, limit: 20 },
        headers: admin()
    })
    return data.airports || []
}

export async function searchHotelsByName(q) {
    // prefix-LIKE search (indexed) proxied to offer-worker — get-paging's %LIKE%
    // full-scans the prod hotels table for 45s+ and times out.
    const { data } = await axios({
        url: `${API}/hotel/hotel-search`,
        method: 'GET',
        params: { q, limit: 20 },
        headers: admin()
    })
    return data.hotels || []
}

// --- fire the offer-worker search, polling until progress === 1 ---
export async function runSearch({ url, headers, body, onTick, maxWaitMs = 60000 }) {
    const started = Date.now()
    let res
    let attempts = 0
    /* eslint-disable no-await-in-loop */
    do {
        attempts += 1
        const resp = await axios({ url, method: 'POST', headers, data: body })
        res = resp.data
        if (onTick) onTick({ progress: res.progress, attempts, res })
        if (res.progress >= 1 || res.ids) break
        await new Promise(r => setTimeout(r, 2000))
    } while (Date.now() - started < maxWaitMs)
    /* eslint-enable no-await-in-loop */
    return { ...res, attempts }
}

// Cross-supplier mapping for one hotel (offer-worker POST /mappings/hotel).
// supplier_code 'EasyGDS' matches the base hotel_id column → returns every
// supplier's mapped id for that hotel (all other hotels in the system).
export async function getHotelMappings({ easygdsId, token, email }) {
    const { data } = await axios({
        url: `${OFFER}/mappings/hotel`,
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'x-key': email,
            'x-access-token': token
        },
        data: { hotel_id: String(easygdsId), supplier_code: 'EasyGDS' }
    })
    return data // { success, data:[{ <supplier>: id|null }], metadata:{ list_suppliers, total_mappings } }
}

// Prebook one offer (V3). offer_id must come from getrooms / search-by-id (bookable).
export async function prebook({ requestId, offerId, token, email }) {
    const { data } = await axios({
        url: `${OFFER}/api/v3/prebook`,
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'x-key': email,
            'x-access-token': token
        },
        data: { request_id: requestId, offer_id: offerId }
    })
    return data
}

// trace-id (or any text containing one) → runnable search curl → parsed payload
export async function traceToPayload(product, raw, version) {
    const input = String(raw).trim()
    // a shopping/booking URL (has expectation=) goes through the server-side
    // hop-1 (URL → trace-id); anything else must contain a 32-hex trace-id
    const isShoppingUrl = /^https?:\/\//.test(input) && input.includes('expectation')
    const params = { ...(version ? { version } : {}) }
    // a link often already carries the trace-id as search_id= — use it directly
    // (hop-1 would fire a brand-new search whose log row may not exist yet)
    let embeddedSearchId = null
    if (isShoppingUrl) {
        try {
            const sid = new URL(input).searchParams.get('search_id')
            if (sid && /^[a-f0-9]{32}$/i.test(sid)) embeddedSearchId = sid
        } catch (e) {
            /* ignore */
        }
    }
    if (embeddedSearchId) {
        params.trace_id = embeddedSearchId
    } else if (isShoppingUrl) {
        params.url = input
    } else {
        const m = input.match(/[a-f0-9]{32}/i)
        if (!m) throw new Error('No trace-id (32-hex) found in the input')
        params.trace_id = m[0]
    }
    const call = p =>
        axios({ url: `${API}/${product}/trace-curl`, method: 'GET', params: p, headers: admin() })
    let data
    try {
        ;({ data } = await call(params))
    } catch (e) {
        // embedded search_id had no log row → fall back to hop-1 (url mode)
        if (embeddedSearchId && isShoppingUrl) {
            ;({ data } = await call({ ...(version ? { version } : {}), url: input }))
        } else {
            throw e
        }
    }
    const curl = data.curl || ''
    const dm = curl.match(/--data\s+'([\s\S]*)'\s*$/)
    if (!dm) throw new Error('Could not parse payload from curl')
    const payload = JSON.parse(dm[1])
    const emailMatch = curl.match(/x-key:\s*([^'\s]+)/)
    const urlMatch = curl.match(/POST\s+'([^']+)'/)
    const traceMatch = curl.match(/Trace-Id:\s*([^'\s]+)/i)
    return {
        traceId: params.trace_id || (traceMatch ? traceMatch[1] : null),
        payload,
        email: emailMatch ? emailMatch[1] : null,
        endpoint: urlMatch ? urlMatch[1] : '',
        curl
    }
}

export const endpoints = { API, OFFER }
